import React, { useEffect, useState } from 'react';
import useApi from '../hooks/useApi';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import Textarea from '../components/ui/Textarea';
import SearchInput from '../components/ui/SearchInput';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { Plus, Edit, Trash2, Building, Hash, Mail, Phone, MapPin, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUIStore } from '../store/useUIStore';

const OfficeManagement = () => {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const { get, post, put, del } = useApi();
  const { tableDensity, setTableDensity } = useUIStore();

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    hierarchyType: 'STANDARD'
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchOffices();
  }, [sortConfig, debouncedSearch]);

  const fetchOffices = async () => {
    setLoading(true);
    try {
      const response = await get(`/offices?sortBy=${sortConfig.key}&order=${sortConfig.direction}&search=${debouncedSearch}`);
      setOffices(response.data);
    } catch (error) {
      console.error('Failed to fetch offices', error);
      toast.error('Failed to fetch offices');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedOffice) {
        await put(`/offices/${selectedOffice.id}`, formData);
        toast.success('Office updated successfully');
      } else {
        await post('/offices', formData);
        toast.success('Office created successfully');
      }
      fetchOffices();
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async () => {
    try {
      await del(`/offices/${selectedOffice.id}`);
      toast.success('Office deleted successfully');
      fetchOffices();
      setIsDeleteOpen(false);
      setSelectedOffice(null);
    } catch (error) {
      console.error('Delete failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete office';
      toast.error(errorMessage);
    }
  };

  const openModal = (office = null) => {
    setSelectedOffice(office);
    if (office) {
      setFormData({
        name: office.name,
        code: office.code,
        address: office.address,
        phone: office.phone,
        email: office.email,
        hierarchyType: office.hierarchyType || 'STANDARD'
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      hierarchyType: 'STANDARD'
    });
    setSelectedOffice(null);
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const columns = [
    { header: 'Office Name', accessorKey: 'name', sortable: true },
    { header: 'Code', accessorKey: 'code', sortable: true },
    { header: 'Email', accessorKey: 'email', sortable: true },
    { header: 'Phone', accessorKey: 'phone', sortable: true },
    { 
      header: 'Actions', 
      accessorKey: 'actions', 
      render: (row) => (
        <div className="flex space-x-2">
          <Button variant="ghost" size="sm" onClick={() => openModal(row)}>
            <Edit className="w-4 h-4 text-blue-600" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            className="text-red-600 hover:text-red-700" 
            onClick={() => { setSelectedOffice(row); setIsDeleteOpen(true); }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    },
  ];

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Office Management</h1>
          <p className="text-sm text-gray-500">Manage office locations and codes</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <Button
              size="sm"
              variant={tableDensity === 'comfortable' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('comfortable')}
            >
              Comfortable
            </Button>
            <Button
              size="sm"
              variant={tableDensity === 'compact' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('compact')}
            >
              Compact
            </Button>
          </div>
          <Button onClick={() => openModal()}>
            <Plus className="w-4 h-4 mr-2" /> Add Office
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <SearchInput 
          value={search} 
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder="Search by name, code, or email..." 
        />
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table 
          columns={columns} 
          data={offices} 
          sortConfig={sortConfig}
          onSort={handleSort}
          density={tableDensity}
        />
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title={selectedOffice ? 'Edit Office' : 'Add New Office'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input 
            label="Office Name"
            name="name"
            icon={Building}
            required 
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. Headquarters"
          />
          
          <Input 
            label="Office Code"
            name="code"
            icon={Hash}
            required 
            value={formData.code}
            onChange={handleChange}
            placeholder="e.g. HQ-01"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input 
              label="Email"
              type="email" 
              name="email"
              icon={Mail}
              value={formData.email}
              onChange={handleChange}
              placeholder="office@example.com"
            />
            
            <Input 
              label="Phone"
              name="phone"
              icon={Phone}
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 234 567 890"
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Hierarchy Type</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <Layers className="h-5 w-5" />
              </div>
              <select
                name="hierarchyType"
                value={formData.hierarchyType}
                onChange={handleChange}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 pl-10 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="STANDARD">Standard (Wing &gt; Dept &gt; Section &gt; User)</option>
                <option value="SIMPLE">Simple (Dept &gt; Section &gt; User)</option>
                <option value="FLAT">Flat (Section &gt; User)</option>
              </select>
            </div>
          </div>

          <Textarea 
            label="Address"
            name="address"
            rows="3"
            value={formData.address}
            onChange={handleChange}
            placeholder="Full office address"
          />

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {selectedOffice ? 'Update Office' : 'Create Office'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete Office"
        message={`Are you sure you want to delete ${selectedOffice?.name}? This action cannot be undone and may affect associated users and records.`}
        confirmText="Delete Office"
        variant="danger"
      />
    </>
  );
};

export default OfficeManagement;
